import { isServer } from "@/services/Helpers";
import StudioApiService from "@/services/StudioApiService";
import { createAsyncThunk, createSelector, createSlice, nanoid, PayloadAction } from "@reduxjs/toolkit";
import PersistenceService from "../../services/list-builder/PersistenceService";
import { GenericModes, STORAGE_KEYS } from "../globals";
import { AsyncStatus, AuthPromptType, ISaveData } from "../types";
import { RootState } from "./store";

interface AppState {
  auth: AuthState;
  openReleaseNotes: boolean;
  genericMode: GenericModes | null;
  showPointsRemaining?: boolean;
  removeAliasMode?: boolean;
  showProduct?: boolean;
  holidayMode?: boolean;
  showHalfDistances?: boolean;
  showBaseSizes?: boolean;
  localSaves: ISaveData[];
  localSavesStatus: AsyncStatus;
  serverSaves: IServerSave[];
  serverSavesStatus: AsyncStatus;
  studioReleaseNotes: IStudioReleaseNote[];
  shareCommunityListOpen: boolean;
  upgradesOpen: boolean;
  lightboxImage: string;
  forceShowBetaSignup?: boolean;
  errorDialog: {
    open: boolean;
    message: string;
    title?: string;
  };
}

export interface IStudioReleaseNote {
  id: number;
  created: string;
  version: string;
  notes: string;
}

interface AuthState {
  userId: string;
  username: string | null;
  loggedIn: boolean;
  showPrompt: AuthPromptType | null;
  roles: string[];
}

interface IServerSave {
  id: string;
  details: ISaveData;
  modifiedAt: string;
  community: boolean;
  userId: string;
}

const genericNamingLocalValue = isServer() ? null : parseInt(localStorage[STORAGE_KEYS.GENERIC_MODE]);
const genericNamingValue = isNaN(genericNamingLocalValue) ? null : genericNamingLocalValue;

const initialState: AppState = {
  auth: {
    userId: null,
    loggedIn: false,
    username: null,
    showPrompt: null,
    roles: []
  },
  openReleaseNotes: false,
  genericMode: genericNamingValue,
  showPointsRemaining: isServer() ? false : localStorage[STORAGE_KEYS.SHOW_POINTS_REMAINING] === "true",
  showProduct: isServer() ? true : localStorage[STORAGE_KEYS.SHOW_PRODUCT] !== "false",
  holidayMode: isServer() ? true : localStorage[STORAGE_KEYS.HOLIDAY_MODE] !== "false",
  showHalfDistances: isServer() ? false : localStorage[STORAGE_KEYS.SHOW_HALF_DISTANCES] === "true",
  removeAliasMode: isServer() ? false : localStorage[STORAGE_KEYS.REMOVE_ALIAS_MODE] === "true",
  showBaseSizes: isServer()
    ? false
    : (() => {
        const showBaseSizes = localStorage.getItem(STORAGE_KEYS.SHOW_BASE_SIZES);
        if (showBaseSizes === null) {
          localStorage.setItem(STORAGE_KEYS.SHOW_BASE_SIZES, "true");
          return true;
        }
        return showBaseSizes === "true";
      })(),
  localSaves: [],
  localSavesStatus: AsyncStatus.idle,
  serverSaves: [],
  serverSavesStatus: AsyncStatus.idle,
  studioReleaseNotes: [],
  shareCommunityListOpen: false,
  upgradesOpen: false,
  lightboxImage: null,
  forceShowBetaSignup: false,
  errorDialog: {
    open: false,
    message: "",
    title: null
  }
};

export const getLocalSaves = createAsyncThunk("app/getLocalSaves", async () => {
  const saves: ISaveData[] = Object.keys(localStorage)
    .filter((k) => k.startsWith("AF_Save"))
    .map((key) => JSON.parse(localStorage[key]) as ISaveData);

  for (let i = saves.length - 1; i >= 0; i--) {
    const save = saves[i];
    const modified = new Date(save.modified);
    const now = new Date();
    const isOld = now.getTime() - modified.getTime() > 5 * 60 * 1000;
    if (save.listPoints == 0 && isOld) {
      saves.splice(i, 1);
      localStorage.removeItem(PersistenceService.getSaveKey(save.id));
      continue;
    }

    // If save id has been set, it's already migrated
    if (save.id) continue;

    // If the list still has the old property, it's not yet migrated
    const oldKey = (save.list as any).creationTime;
    if (oldKey) {
      // Ensure the list has an id
      if (!save.list.id) save.list.id = nanoid(12);

      // Remove old key
      delete (save.list as any).creationTime;

      // Assign new save id prop
      save.id = save.list.id;

      // Add entry with the new key
      const newKey = PersistenceService.getSaveKey(save.list.id);
      localStorage[newKey] = JSON.stringify(save);

      // Remove entry with old key
      localStorage.removeItem(PersistenceService.getSaveKey(oldKey));
    }
  }

  return saves;
});

export const getServerSaves = createAsyncThunk(
  "app/getServerSaves",
  async () => {
    const res = await fetch("/api/account/lists");
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  },
  {
    condition(_, { getState }) {
      const { serverSavesStatus } = (getState() as RootState).app;
      return serverSavesStatus !== AsyncStatus.loading;
    }
  }
);

export const getReleaseNotes = createAsyncThunk("app/getReleaseNotes", async (lastVersion?: number) => {
  const notes = await StudioApiService.getReleaseNotes(lastVersion);
  return notes;
});

export const pushSave = createAsyncThunk("app/pushSave", async (saveData: ISaveData) => {
  saveData.list.cloudModified = saveData.list.modified;

  const res = await fetch("/api/account/lists/" + saveData.list.id, {
    method: "POST",
    body: JSON.stringify({ save: saveData })
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as IServerSave;
});

export const shareCommunityList = createAsyncThunk(
  "app/shareCommunityList",
  async (query: { listId: string; community: boolean }) => {
    const { listId } = query;

    const res = await fetch(`/api/community-lists/share/${listId}`, { method: "POST" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res;
  }
);

export const unshareCommunityList = createAsyncThunk(
  "app/unshareCommunityList",
  async (query: { listId: string }) => {
    const { listId } = query;

    const res = await fetch(`/api/community-lists/unshare/${listId}`, { method: "POST" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res;
  }
);

export const deleteSave = createAsyncThunk("app/deleteSave", async (save: ISaveData) => {
  try {
    const res = await fetch("/api/account/lists/" + save.id, { method: "DELETE" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
  } catch (e) {
    console.error(e);
  }

  PersistenceService.deleteSave(save.id);

  return save.id;
});

export const logout = createAsyncThunk("app/logout", async () => {
  const res = await fetch("/api/auth/logout");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return {
    userId: null,
    loggedIn: false,
    username: null,
    showPrompt: null,
    roles: []
  };
});

const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setUpgradesOpen(state, action: PayloadAction<boolean>) {
      state.upgradesOpen = action.payload;
    },
    setOpenReleaseNotes(state, action: PayloadAction<boolean>) {
      state.openReleaseNotes = action.payload;
    },
    setShowAuthPrompt(state, action: PayloadAction<AuthPromptType | null>) {
      state.auth.showPrompt = action.payload;
    },
    setGenericMode(state, action: PayloadAction<GenericModes | null>) {
      const newValue = action.payload === state.genericMode ? null : action.payload;
      state.genericMode = newValue;
      localStorage[STORAGE_KEYS.GENERIC_MODE] = newValue;
    },
    setShowPointsRemaining(state, action: PayloadAction<boolean>) {
      state.showPointsRemaining = action.payload;
      localStorage[STORAGE_KEYS.SHOW_POINTS_REMAINING] = action.payload;
    },
    setShowProduct(state, action: PayloadAction<boolean>) {
      state.showProduct = action.payload;
      localStorage[STORAGE_KEYS.SHOW_PRODUCT] = action.payload;
    },
    setHolidayMode(state, action: PayloadAction<boolean>) {
      state.holidayMode = action.payload;
      localStorage[STORAGE_KEYS.HOLIDAY_MODE] = action.payload;
    },
    setShowHalfDistances(state, action: PayloadAction<boolean>) {
      state.showHalfDistances = action.payload;
      localStorage[STORAGE_KEYS.SHOW_HALF_DISTANCES] = action.payload;
    },
    setRemoveAliasMode(state, action: PayloadAction<boolean>) {
      state.removeAliasMode = action.payload;
      localStorage[STORAGE_KEYS.REMOVE_ALIAS_MODE] = action.payload;
    },
    setShowBaseSizes(state, action: PayloadAction<boolean>) {
      state.showBaseSizes = action.payload;
      localStorage[STORAGE_KEYS.SHOW_BASE_SIZES] = action.payload;
    },
    setShareCommunityListOpen(state, action: PayloadAction<boolean>) {
      state.shareCommunityListOpen = action.payload;
    },

    setAuthentication(
      state,
      action: PayloadAction<{ userId: string; isAuth: boolean; username: string; roles: string[] }>
    ) {
      const { userId, isAuth, username, roles } = action.payload;
      state.auth.userId = userId;
      state.auth.username = username;
      state.auth.roles = roles;
      state.auth.loggedIn = isAuth;
    },

    setLightboxImage(state, action: PayloadAction<string>) {
      state.lightboxImage = action.payload;
    },
    showErrorDialog(state, action: PayloadAction<{ message: string; title?: string }>) {
      const { message, title } = action.payload;
      state.errorDialog = { open: true, message, title };
    },
    hideErrorDialog(state) {
      state.errorDialog = {
        open: false,
        message: "",
        title: null
      };
    },
    setForceShowBetaSignup(state, action: PayloadAction<boolean>) {
      state.forceShowBetaSignup = action.payload;
    }
  },
  selectors: {
    selectLocalSaves: (state) => state.localSaves,
    selectServerSaves: (state) => state.serverSaves,
    selectServerSavesStatus: (state) => state.serverSavesStatus
  },
  extraReducers(builder) {
    builder
      .addCase(getLocalSaves.pending, (state) => {
        return { ...state, localSavesStatus: AsyncStatus.loading };
      })
      .addCase(getLocalSaves.fulfilled, (state, action) => {
        return { ...state, localSaves: action.payload, localSavesStatus: AsyncStatus.succeeded };
      });
    builder
      .addCase(getServerSaves.pending, (state) => {
        return { ...state, serverSavesStatus: AsyncStatus.loading };
      })
      .addCase(getServerSaves.rejected, (state) => {
        return { ...state, serverSavesStatus: AsyncStatus.failed };
      })
      .addCase(getServerSaves.fulfilled, (state, action) => {
        return {
          ...state,
          serverSaves: action.payload,
          serverSavesStatus: AsyncStatus.succeeded
        };
      });
    builder.addCase(logout.fulfilled, (state, action) => {
      state.auth = action.payload;
    });
    builder.addCase(deleteSave.fulfilled, (state, action) => {
      const saveId = action.payload;

      const remove = (saves) => {
        const index = saves.findIndex((x) => x.id === saveId);
        if (index >= 0) saves.splice(index, 1);
      };
      remove(state.localSaves);
      remove(state.serverSaves);
    });
    builder.addCase(getReleaseNotes.fulfilled, (state, action) => {
      const notes = action.payload;
      state.studioReleaseNotes = notes;
    });
    builder.addCase(pushSave.fulfilled, (state, action) => {
      state.serverSaves = state.serverSaves.filter((x) => x.id !== action.payload.id);
      state.serverSaves.push(action.payload);
    });
  }
});

// Action creators are generated for each case reducer function
export const {
  setUpgradesOpen,
  setOpenReleaseNotes,
  setShowAuthPrompt,
  setGenericMode,
  setShowPointsRemaining,
  setShowProduct,
  setHolidayMode,
  setShareCommunityListOpen,
  setShowHalfDistances,
  setShowBaseSizes,
  setRemoveAliasMode,
  setAuthentication,
  setLightboxImage,
  showErrorDialog,
  hideErrorDialog,
  setForceShowBetaSignup
} = appSlice.actions;

export const { selectServerSaves, selectServerSavesStatus } = appSlice.selectors;
const { selectLocalSaves } = appSlice.selectors;

export const selectAllSaves = createSelector(
  [selectLocalSaves, selectServerSaves, selectServerSavesStatus],
  (localSaves, serverSaves, serverSavesStatus) => {
    const isLoaded = [AsyncStatus.succeeded, AsyncStatus.failed].includes(serverSavesStatus);
    const hasServerSaves = serverSavesStatus === AsyncStatus.succeeded && serverSaves.length > 0;
    const serverSaveData = serverSaves.map((x) => ({
      ...x.details,
      community: x.community,
      userId: x.userId
    })) as ISaveData[];
    const result = {
      saves: hasServerSaves
        ? serverSaveData.concat(localSaves.filter((x) => !serverSaveData.some((y) => y.id === x.id)))
        : localSaves,
      loaded: isLoaded
    };
    return result;
  }
);

export default appSlice.reducer;
